import { useState, useEffect, useCallback, useRef } from 'react';
import residentService from '../services/residentService';
import authService from '../services/authService';
import { isUuid } from '../contexts/TenantContext';
import {
  ResidentDTO,
  CreateResidentRequest,
  PageResponse,
  UploadedDocument,
  ResidentDocumentDTO
} from '../types/resident.types';
import { useTenant } from '../contexts/TenantContext';

interface UseResidentsReturn {
  // State
  residents: ResidentDTO[];
  selectedResident: ResidentDTO | null;
  loading: boolean;
  saving: boolean;
  uploadingDocuments: boolean;
  error: string | null;
  totalElements: number;
  totalPages: number;
  currentPage: number;
  residentDocuments: ResidentDocumentDTO[];
  viewResidentDocument: (documentId: string, fileName?: string) => Promise<void>;
  hasFetched: boolean;

  // Actions
  fetchResidents: (page?: number, size?: number, facilityId?: string | null, status?: string) => Promise<void>;
  fetchResidentsByFacility: (facilityId: string, page?: number, size?: number, status?: string) => Promise<void>;
  fetchResidentsByAdmissionType: (type: string, facilityId?: string | null) => Promise<void>;
  searchResidents: (query: string, page?: number, size?: number) => Promise<void>;
  getResidentById: (id: string, silent?: boolean) => Promise<ResidentDTO | null>;
  createResident: (data: CreateResidentRequest) => Promise<ResidentDTO>;
  updateResident: (id: string, data: Partial<ResidentDTO>) => Promise<ResidentDTO>;
  toggleResidentStatus: (id: string) => Promise<ResidentDTO>;
  dischargeResident: (id: string, reason: string) => Promise<void>;
  deleteResident: (id: string) => Promise<void>;
  quickSearchResidents: (search: string, status?: string) => Promise<void>;
  uploadResidentDocuments: (residentId: string, documents: UploadedDocument[]) => Promise<void>;
  setSelectedResident: (resident: ResidentDTO | null) => void;
  clearError: () => void;
  fetchResidentDocuments: (residentId: string) => Promise<void>;
  deleteResidentDocument: (residentId: string, documentId: string) => Promise<void>;
  fetchActiveLookup: (facilityId: string) => Promise<void>;

  // Pagination
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

/**
 * Custom hook for managing residents
 */

// Helper to resolve valid facility UUID, falling back to JWT claims if candidate is missing or non-UUID (e.g. 'carex')
const resolveValidFacilityId = (candidate?: string | null): string => {
  if (candidate && isUuid(candidate)) return candidate;
  const fromToken = authService.getFacilityIdFromToken();
  if (fromToken && isUuid(fromToken)) return fromToken;
  const fromStorage = localStorage.getItem('currentTenantId');
  if (fromStorage && isUuid(fromStorage)) return fromStorage;
  return isUuid(candidate) ? candidate! : (fromToken || '');
};

export const useResidents = (): UseResidentsReturn => {
  const { currentTenantId } = useTenant();
  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [residentDocuments, setResidentDocuments] = useState<ResidentDocumentDTO[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingDocuments, setUploadingDocuments] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const tenantContext = useTenant();
  const quickSearchAbortControllerRef = useRef<AbortController | null>(null);

  const fetchResidents = useCallback(
    async (
      page: number = 0,
      size: number = 15,
      selectedFacilityId?: string | null,
      status?: string
    ) => {
      try {
        setLoading(true);
        setError(null);

        const facilityId = resolveValidFacilityId(selectedFacilityId || currentTenantId) || null;

        const response = await residentService.getAllResidents(
          page,
          size,
          facilityId,
          status
        );

        setResidents(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        setCurrentPage(response.number);

      } catch (err: any) {
        console.error("Error fetching residents:", err);
        setError(err.response?.data?.message || "Failed to load residents");
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    [currentTenantId]
  );


  const fetchResidentDocuments = useCallback(async (residentId: string) => {
    try {
      const docs = await residentService.getResidentDocuments(residentId);
      setResidentDocuments(docs);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.message || 'Failed to load documents');
    }
  }, []);

  const fetchResidentsByFacility = useCallback(
    async (
      facilityId: string,
      page: number = 0,
      size: number = 15,
      status?: string
    ) => {
      try {
        setLoading(true);
        setError(null);
        if (!facilityId) return;

        const response = await residentService.getAllResidents(
          page,
          size,
          facilityId,
          status
        );
        setResidents(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        setCurrentPage(response.number);
      } catch (err: any) {
        console.error('Error fetching residents by facility:', err);
        setError(err.response?.data?.message || 'Failed to load facility residents');
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    []
  );

  const fetchResidentsByAdmissionType = useCallback(
    async (type: string, facilityId?: string | null) => {
      try {
        setLoading(true);
        setError(null);

        const targetFacilityId = facilityId || currentTenantId;

        if (!targetFacilityId) {
          throw new Error('Facility ID is required');
        }

        const data = await residentService.getResidentsByAdmissionType(targetFacilityId, type);
        setResidents(data);
        setTotalElements(data.length);
        setTotalPages(1); // Since this endpoint returns a list, effectively 1 page
        setCurrentPage(0);
      } catch (err: any) {
        console.error('Error fetching residents by type:', err);
        setError(err.response?.data?.message || 'Failed to load residents by type');
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    [currentTenantId]
  );

  const searchResidents = useCallback(
    async (query: string, page: number = 0, size: number = 20) => {
      if (!query.trim()) {
        fetchResidents(page, size);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response: PageResponse<ResidentDTO> = await residentService.searchResidents(
          query,
          page,
          size
        );

        setResidents(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        setCurrentPage(response.number);
      } catch (err: any) {
        console.error('Error searching residents:', err);
        setError(err.response?.data?.message || 'Failed to search residents');
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    [fetchResidents]
  );

  const quickSearchResidents = useCallback(
    async (search: string, status?: string) => {
      try {
        if (quickSearchAbortControllerRef.current) {
          quickSearchAbortControllerRef.current.abort();
        }
        quickSearchAbortControllerRef.current = new AbortController();

        setLoading(true);
        setError(null);

        const facilityId = resolveValidFacilityId(currentTenantId);
        if (!facilityId) {
          throw new Error('Facility ID is required');
        }

        const data = await residentService.quickSearch(
          facilityId,
          search,
          status,
          quickSearchAbortControllerRef.current.signal
        );
        setResidents(data);
        setTotalElements(data.length);
        setTotalPages(1);
        setCurrentPage(0);
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Error in quick search:', err);
        setError(err.response?.data?.message || 'Failed to perform quick search');
      } finally {
        // If aborted, we don't want to stop the loading state for the next request
        if (!quickSearchAbortControllerRef.current?.signal.aborted) {
          setLoading(false);
          setHasFetched(true);
        }
      }
    },
    [currentTenantId]
  );

  const fetchActiveLookup = useCallback(
    async (facilityId: string) => {
      try {
        setLoading(true);
        setError(null);
        const data = await residentService.getActiveLookup(facilityId);
        setResidents(data || []);
        setTotalElements(data?.length || 0);
        setTotalPages(1);
        setCurrentPage(0);
      } catch (err: any) {
        console.error('Error in active lookup:', err);
        setError(err.response?.data?.message || 'Failed to perform active lookup');
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    []
  );



  const viewResidentDocument = useCallback(
    async (documentId: string, fileName?: string) => {
      try {
        const blob = await residentService.downloadDocument(documentId);

        const url = window.URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");

        if (!opened) {
          // fallback: forced download
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || "document";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }

        // Revoke after some time
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 30_000);

      } catch (err: any) {
        console.error("Error:", err);
      }
    },
    []
  );




  const getResidentById = useCallback(async (id: string, silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const resident = await residentService.getResidentById(id);
      setSelectedResident(resident);

      // Also add to the main residents list if not already there
      setResidents(prev => {
        if (prev.some(r => r.id === resident.id)) return prev;
        return [...prev, resident];
      });

      return resident;
    } catch (err: any) {
      console.error('Error fetching resident:', err);
      if (!silent) setError(err.response?.data?.message || 'Failed to load resident');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const createResident = useCallback(
    async (data: CreateResidentRequest): Promise<ResidentDTO> => {
      try {
        setSaving(true);
        setError(null);
        const facilityId = resolveValidFacilityId(tenantContext.currentTenantId);

        if (!facilityId) {
          setError("Please select a facility before creating a resident.");
          throw new Error("Facility not selected.");
        }

        const newResident = await residentService.createResident(data, facilityId);




        await fetchResidents(currentPage, pageSize);

        return newResident;
      } catch (err: any) {
        console.error('Error creating resident:', err);
        setError(err.response?.data?.message || 'Failed to create resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchResidents, currentPage, pageSize, currentTenantId]
  );

  const updateResident = useCallback(
    async (id: string, data: Partial<ResidentDTO>): Promise<ResidentDTO> => {
      try {
        setSaving(true);
        setError(null);

        const updated = await residentService.updateResident(id, data);

        setResidents((prev) =>
          prev.map((r) => (r.id === id ? updated : r))
        );

        if (selectedResident?.id === id) {
          setSelectedResident(updated);
        }

        return updated;
      } catch (err: any) {
        console.error('Error updating resident:', err);
        setError(err.response?.data?.message || 'Failed to update resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident]
  );

  const dischargeResident = useCallback(
    async (id: string, reason: string) => {
      try {
        setSaving(true);
        setError(null);

        const discharged = await residentService.dischargeResident(id, reason);

        setResidents((prev) =>
          prev.map((r) => (r.id === id ? discharged : r))
        );

        if (selectedResident?.id === id) {
          setSelectedResident(discharged);
        }
      } catch (err: any) {
        console.error('Error discharging resident:', err);
        setError(err.response?.data?.message || 'Failed to discharge resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident]
  );

  const deleteResident = useCallback(
    async (id: string) => {
      try {
        setSaving(true);
        setError(null);

        await residentService.deleteResident(id);

        setResidents((prev) => prev.filter((r) => r.id !== id));

        if (selectedResident?.id === id) {
          setSelectedResident(null);
        }

        await fetchResidents(currentPage, pageSize);
      } catch (err: any) {
        console.error('Error deleting resident:', err);
        setError(err.response?.data?.message || 'Failed to delete resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident, fetchResidents, currentPage, pageSize]
  );

  const uploadResidentDocuments = useCallback(
    async (residentId: string, documents: UploadedDocument[]) => {
      try {
        setUploadingDocuments(true);
        setError(null);

        for (const doc of documents) {
          // Skip existing documents (already stored on backend)
          if (!doc.file) {
            continue;
          }

          const formData = new FormData();
          formData.append('file', doc.file);
          formData.append('documentName', doc.name);
          if (doc.createdAt) {
            formData.append('createdAt', doc.createdAt);
          }
          if (doc.createdBy) {
            formData.append('createdBy', doc.createdBy);
            formData.append('created_by', doc.createdBy);
            formData.append('author', doc.createdBy);
          }
          if (doc.documentType) {
            formData.append('documentType', doc.documentType);
          }

          await residentService.uploadDocument(residentId, formData);

        }
      } catch (err: any) {
        console.error('Document upload failed:', err);
        setError(err.response?.data?.message || 'Failed to upload documents');
        throw err;
      } finally {
        setUploadingDocuments(false);
      }
    },
    []
  );

  const deleteResidentDocument = useCallback(
    async (residentId: string, documentId: string) => {
      try {
        setSaving(true);
        setError(null);
        await residentService.deleteDocument(residentId, documentId);
        await fetchResidentDocuments(residentId);
      } catch (err: any) {
        console.error('Error deleting document:', err);
        setError(err.response?.data?.message || 'Failed to delete document');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchResidentDocuments]
  );

  const setPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchResidents(page, pageSize);
    },
    [fetchResidents, pageSize]
  );

  const handleSetPageSize = useCallback(
    (size: number) => {
      setPageSize(size);
      setCurrentPage(0);
      fetchResidents(0, size);
    },
    [fetchResidents]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);


  const toggleResidentStatus = useCallback(
    async (id: string): Promise<ResidentDTO> => {
      try {
        setSaving(true);
        setError(null);

        const updated = await residentService.toggleResidentStatus(id);

        setResidents((prev) =>
          prev.map((r) => (r.id === id ? { ...r, ...updated } : r))
        );

        if (selectedResident?.id === id) {
          setSelectedResident((prev) => (prev ? { ...prev, ...updated } : null));
        }

        return updated;
      } catch (err: any) {
        console.error('Error toggling resident status:', err);
        const backendMessage = err.response?.data?.message || err.response?.data?.error || err.message;
        setError(backendMessage || 'Failed to toggle resident status');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident]
  );

  return {
    residents,
    selectedResident,
    loading,
    saving,
    uploadingDocuments,
    error,
    totalElements,
    totalPages,
    currentPage,
    residentDocuments,
    viewResidentDocument,
    fetchResidents,
    fetchResidentsByFacility,
    fetchResidentsByAdmissionType,
    searchResidents,
    quickSearchResidents,
    getResidentById,
    createResident,
    updateResident,
    toggleResidentStatus,
    dischargeResident,
    deleteResident,
    uploadResidentDocuments,
    deleteResidentDocument,
    setSelectedResident,
    clearError,
    fetchResidentDocuments,
    setPage,
    setPageSize: handleSetPageSize,
    fetchActiveLookup,
    hasFetched,
  };

};
